import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";

import { ReactComponent as WarningTriangleIcon } from "atoms/icons/warning_triangle.svg";

import type { BulkSelection } from "@/hooks/useBulkSelection";

import { Checkbox } from "../Checkbox";
import { Tooltip } from "../Tooltip";

/** Matches the label every bulk-select table renders on the ineligible-row icon today. */
const DEFAULT_INELIGIBLE_ICON_ARIA_LABEL = "Incomplete draft";

const INELIGIBLE_TRIGGER_CLASS_NAME = "m-2.5 inline-flex h-6 w-6 items-center justify-center";

export type BulkSelectColumnOptions<T extends { id: string }> = {
  selection: BulkSelection<T>;
  headerAriaLabel: string;
  getRowAriaLabel: (row: T) => string;
  /** Rows this returns `false` for render an empty cell (no checkbox, no warning). */
  isRowRendered?: (row: T) => boolean;
  /** When `true`, ineligible rows render the warning tooltip instead of a disabled checkbox. */
  showIneligibleWarning: boolean;
  /**
   * Required whenever `showIneligibleWarning` can be `true` — callers that pass a literal
   * `false` omit it. Not modelled as a discriminated union because two callers pass a runtime
   * boolean (`selection.isPublishMode`), which no `true`/`false` branch accepts; the warning
   * branch throws instead of rendering an empty tooltip.
   */
  getIneligibleTooltipContent?: (row: T) => string;
  ineligibleTriggerElement?: "span" | "button";
  /** Accessible label on the warning icon itself. Defaults to "Incomplete draft". */
  ineligibleIconAriaLabel?: string;
  checkboxName?: (row: T) => string;
  headerCheckboxName?: string;
  size?: number;
  enableResizing?: boolean;
  enableSorting?: boolean;
};

/**
 * Builds the shared `bulkSelect` DataGrid column. Cell rendering has five shapes:
 * empty (`isRowRendered` false), checkbox, disabled checkbox, tooltip + `<span>`, and
 * tooltip + disabled `<button>`. Test ids (`select-all-drafts`, `select-draft-${id}`,
 * `incomplete-draft-warning-${id}`) are part of the contract.
 */
export function buildBulkSelectColumn<T extends { id: string }>({
  selection,
  headerAriaLabel,
  getRowAriaLabel,
  isRowRendered,
  showIneligibleWarning,
  getIneligibleTooltipContent,
  ineligibleTriggerElement = "span",
  ineligibleIconAriaLabel = DEFAULT_INELIGIBLE_ICON_ARIA_LABEL,
  checkboxName,
  headerCheckboxName,
  size = 80,
  enableResizing,
  enableSorting,
}: BulkSelectColumnOptions<T>): ColumnDef<T, unknown> {
  const columnHelper = createColumnHelper<T>();

  return columnHelper.display({
    id: "bulkSelect",
    size,
    ...(enableResizing === undefined ? {} : { enableResizing }),
    ...(enableSorting === undefined ? {} : { enableSorting }),
    header: () => (
      <Checkbox
        aria-label={headerAriaLabel}
        checked={selection.headerChecked}
        data-testid="select-all-drafts"
        disabled={selection.headerDisabled}
        name={headerCheckboxName}
        onCheckedChange={selection.toggleAll}
      />
    ),
    cell: (ctx) => {
      const row = ctx.row.original;

      if (isRowRendered && !isRowRendered(row)) {
        return null;
      }

      const selectable = selection.isRowSelectable(row);

      if (!selectable && showIneligibleWarning) {
        if (!getIneligibleTooltipContent) {
          throw new Error(
            "buildBulkSelectColumn: showIneligibleWarning is true but getIneligibleTooltipContent was not supplied, so the warning tooltip has no copy to render.",
          );
        }

        const tooltipContent = getIneligibleTooltipContent(row);
        const icon = (
          <WarningTriangleIcon aria-label={ineligibleIconAriaLabel} className="h-4 w-4" />
        );

        return (
          <Tooltip
            content={tooltipContent}
            placement="right"
            trigger={
              ineligibleTriggerElement === "button" ? (
                <button
                  aria-label={tooltipContent}
                  className={INELIGIBLE_TRIGGER_CLASS_NAME}
                  data-testid={`incomplete-draft-warning-${row.id}`}
                  disabled
                  type="button"
                >
                  {icon}
                </button>
              ) : (
                <span
                  className={INELIGIBLE_TRIGGER_CLASS_NAME}
                  data-testid={`incomplete-draft-warning-${row.id}`}
                >
                  {icon}
                </span>
              )
            }
          />
        );
      }

      return (
        <Checkbox
          aria-label={getRowAriaLabel(row)}
          checked={selection.isSelected(row.id)}
          data-testid={`select-draft-${row.id}`}
          disabled={!selectable}
          name={checkboxName?.(row)}
          onCheckedChange={() => selection.toggleRow(row.id)}
        />
      );
    },
  });
}

export type ColumnVisibilityFilterOptions = {
  isBulkMode: boolean;
  /** Only meaningful on grids that have a `changeOrder` column. */
  changeOrderMode?: boolean;
  /** The caller's raw "drafts segment is active" signal, before the bulk-mode guard. */
  showReviewAndPublish?: boolean;
};

/**
 * The `.filter()` predicate every bulk-select grid applies to its column list:
 * `bulkSelect` only in bulk mode, `actions` only outside it, `changeOrder` only in
 * reorder mode, and `reviewAndPublish` only on the drafts segment outside both modes.
 */
export function buildColumnVisibilityFilter({
  isBulkMode,
  changeOrderMode = false,
  showReviewAndPublish = false,
}: ColumnVisibilityFilterOptions): (column: { id?: string }) => boolean {
  return ({ id }) => {
    if (id === "bulkSelect") return isBulkMode;
    if (id === "changeOrder") return changeOrderMode;
    if (id === "actions") return !isBulkMode;
    if (id === "reviewAndPublish") return showReviewAndPublish && !isBulkMode && !changeOrderMode;
    return true;
  };
}
